import { useCallback, useState } from 'react';
import { Location } from '@spa/components/storefinder/interfaces';
export interface UseUserLocation {
  location: Location | undefined;
  error: string | null;
  isLoading: boolean;
  getUserLocation: () => Promise<Location>;
}

const getPosition = (options?: PositionOptions): Promise<GeolocationPosition> => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported by your browser.'));
      return;
    }
    navigator.geolocation.getCurrentPosition(resolve, reject, options);
  });
};

const getErrorMessage = (error: unknown): string => {
  if (
    typeof GeolocationPositionError !== 'undefined' &&
    error instanceof GeolocationPositionError
  ) {
    switch (error.code) {
      case error.PERMISSION_DENIED:
        return 'User denied the request for Geolocation.';
      case error.POSITION_UNAVAILABLE:
        return 'Location information is unavailable.';
      case error.TIMEOUT:
        return 'The request to get user location timed out.';
    }
  }
  return error instanceof Error ? error.message : 'An unknown error occurred.';
};

export const useUserLocation = (): UseUserLocation => {
  const [location, setLocation] = useState<Location | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const getUserLocation = useCallback(async (): Promise<Location> => {
    setIsLoading(true);
    setError(null);

    try {
      const position = await getPosition({ timeout: 10000 });
      const { latitude, longitude } = position.coords;
      const newLocation = { lat: latitude, lng: longitude };

      setLocation(newLocation);
      return newLocation;
    } catch (err) {
      const errorMsg = getErrorMessage(err);
      setError(errorMsg);
      throw new Error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { location, error, isLoading, getUserLocation };
};
